import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { UsageQuery } from "@/api";
import { appState } from "@/app/appState";
import { ActionHandler } from "@/components/UI/tools";
import { Field, Section, TextField } from "../ui";
import { useCostByUserChart } from "./charts/costByUser";
import { useNetByUserChart } from "./charts/netByUser";
import { useRevenueByUserChart } from "./charts/revenueByUser";
import { chartVisible, searchTerms } from "./table";
import { UsageChart } from "./views";

const PRESET_HOURS = { "24h": 24, "7d": 7 * 24, "30d": 30 * 24, "90d": 90 * 24 } as const;
type Preset = keyof typeof PRESET_HOURS | "custom";
const PRESETS: Preset[] = ["24h", "7d", "30d", "90d", "custom"];

const localDate = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const daysAgo = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
};

/**
 * Usage of every user in the organisation subtree rooted at `orgId`. The scope
 * bar builds one UsageQuery that every chart is fetched with; the search only
 * filters rows that are already loaded.
 */
export const UsageSection = observer(({ orgId }: { orgId: number }) => {
    const [preset, setPreset] = useState<Preset>("30d");
    // Custom range in local dates; `to` is inclusive.
    const [from, setFrom] = useState(() => localDate(daysAgo(30)));
    const [to, setTo] = useState(() => localDate(new Date()));
    const [search, setSearch] = useState("");
    // Presets are relative to "now"; it only moves on refresh, or every chart would refetch on each render.
    const [now, setNow] = useState(() => Date.now());

    const query = useMemo<UsageQuery | undefined>(() => {
        let start: Date;
        let end: Date;
        if (preset === "custom") {
            start = new Date(`${from}T00:00`);
            end = new Date(`${to}T00:00`);
            end.setDate(end.getDate() + 1);
            if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return undefined;
        } else {
            end = new Date(now);
            start = new Date(now - PRESET_HOURS[preset] * 3600 * 1000);
        }
        return { org_id: orgId, start: start.toISOString(), end: end.toISOString() };
    }, [orgId, preset, from, to, now]);

    const charts = [useCostByUserChart(query), useRevenueByUserChart(query), useNetByUserChart(query)];
    const terms = searchTerms(search);
    const shown = charts.filter((chart) => chartVisible(chart, terms));

    return (
        <div className="flex flex-col gap-2 w-full">
            <Section
                title={appState.t("usage.scope")}
                hint={appState.t("usage.scopeHint")}
                action={
                    <ActionHandler title={appState.t("usage.refresh")} funq={() => setNow(Date.now())} className="btn-sm">
                        {appState.t("usage.refresh")}
                    </ActionHandler>
                }
            >
                <div className="row-form">
                    <div role="tablist" className="tabs">
                        {PRESETS.map((p) => (
                            <button
                                key={p}
                                role="tab"
                                aria-selected={p === preset}
                                className={`tab ${p === preset ? "tab-active" : ""}`}
                                onClick={() => {
                                    setPreset(p);
                                    setNow(Date.now());
                                }}
                            >
                                {appState.t(`usage.range.${p}`)}
                            </button>
                        ))}
                    </div>
                    {preset === "custom" && (
                        <div className="flex flex-row flex-wrap gap-1">
                            <Field label={appState.t("usage.from")}>
                                <TextField type="date" value={from} onChange={setFrom} />
                            </Field>
                            <Field label={appState.t("usage.to")}>
                                <TextField type="date" value={to} onChange={setTo} />
                            </Field>
                        </div>
                    )}
                    {!query && <p className="detail-text">{appState.t("usage.invalidRange")}</p>}
                    <input
                        className="search"
                        type="search"
                        value={search}
                        placeholder={appState.t("usage.search")}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </Section>

            {shown.map((chart) => (
                <UsageChart key={chart.key} chart={chart} terms={terms} />
            ))}
            {!shown.length && <p className="empty-state">{appState.t("usage.noMatches")}</p>}
        </div>
    );
});
